import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, timeout, finalize } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../app.config';
import { LoadingService } from '../shared/loading.service';
import { getAuthToken } from '../auth.interceptor';
import { IEmailValidation } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { TokenService } from './token.service';
import { LoginAttempt } from '../third-party-login/third-party-login.component';


export interface EmailDocumentRequest {
    requestType: string;
    subject: string;
    esignFlag: boolean;
    program: string;
    queryParam: Array<{ attachmentDocId: string }>;
    email: string;
    token: string;
}

export interface ThirdPartyLinkEmailRequest {
    email: string;
    policyNumber: string;
    requestType: string;
    emailTemplate: string;
    ThirdPartyURL: string;
    token: string;
    subject: string;
}

export interface PortletLinkEmailRequest {
    email: string;
    policyNumber: string;
    requestType: string;
    emailTemplate: string;
    PaymentURL: string;
    token: string;
    subject: string;
}

export interface PaymentLinkEmailRequest {
    email: string;
    policyNumber: string;
    requestType: string;
    emailTemplate: string;
    PaymentURL: string;
    token: string;
    subject: string;
    zipcode?: string;
}

export interface EmailResponse {
    status: string;
    message?: string;
    statusCode?: number;
}

interface EmailVerificationResponse {
    status: string;
    sub_status?: string;
}

export interface ContactEmailRequest {
    sendFrom: string;
    sendToEmail: string;
    subject: string;
    messageBody: string;
}


@Injectable({
    providedIn: 'root'
})
export class EmailService implements IEmailValidation {
    private readonly TIMEOUT = 60000;
    private readonly VERIFICATION_TIMEOUT = 20000;
    private readonly VALID_STATUSES = ['valid', 'abuse', 'catch-all'];
    private readonly headers = new HttpHeaders({
        'x-api-key': this.config.apiKey,
        'Content-Type': 'application/json'
    });

    constructor(
        private http: HttpClient,
        private loader: LoadingService,
        @Inject(APP_CONFIG) private config: AppConfig,
        private tokenService: TokenService
    ) {}

    sendDocumentEmail(emailRequest: EmailDocumentRequest): Observable<EmailResponse> {
        this.loader.showLoading();

        return this.http.post<EmailResponse>(this.config.emailUrl, emailRequest, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map(response => {
                if (response.status === 'Success' || response.statusCode === 200) return response;
                throw new Error(response.message || 'Failed to send email');
            }),
            catchError(error => throwError(() => error)),
            finalize(() => this.loader.hideLoading())
        );
    }

    validateEmail(email: string): Observable<any> {
        if (!email) return of(null);

        const params = new HttpParams()
            .set('api_key', this.config.emailVerificationKey)
            .set('email', email)
            .set('ip_address', '')
            .set('nocache', Date.now().toString());

        return this.http.get<EmailVerificationResponse>(this.config.emailVerificationURL, { params }).pipe(
            timeout(this.VERIFICATION_TIMEOUT),
            map(res => this.isValidEmail(res) ? null : {
                emailService: true,
                status: res.status,
                sub_status: res.sub_status,
                message: this.getErrorMessage(res)
            }),
            catchError(() => of(null))
        );
    }

    validateEmailFormat(email: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    buildSingleDocumentEmailRequest(
        document: { attachmentDocId?: string; fileName: string },
        recipientEmail: string
    ): EmailDocumentRequest {
        return this.buildEmailRequest('Single-Document', `Document: ${document.fileName}`, [document], recipientEmail);
    }

    buildMultipleDocumentEmailRequest(
        documents: Array<{ attachmentDocId?: string; fileName: string }>,
        recipientEmail: string
    ): EmailDocumentRequest {
        return this.buildEmailRequest('Multi-Document', 'Documents for your PSIC policy', documents, recipientEmail);
    }

    buildThirdPartyLinkEmailRequest(
        recipientEmail: string,
        policyNumber: string,
        thirdPartyURL: string
    ): ThirdPartyLinkEmailRequest {
        const token = this.tokenService.getToken() || getAuthToken();
        
        return {
            email: recipientEmail,
            policyNumber,
            requestType: 'Third-Party-Link',
            emailTemplate: 'ThirdParty_Link_External',
            ThirdPartyURL: thirdPartyURL,
            token,
            subject: 'Your PSIC requested access link for policy management is ready'
        };
    }

    buildPortletLinkEmailRequest(
        recipientEmail: string,
        policyNumber: string,
        portletURL: string
    ): PortletLinkEmailRequest {
        const token = this.tokenService.getToken() || getAuthToken();

        return {
            email: recipientEmail,
            policyNumber,
            requestType: 'Payment-Link',
            emailTemplate: 'Payment_Link_External',
            PaymentURL: portletURL,
            token,
            subject: 'Your PSIC requested portlet link for policy management is ready',
        };
    }

    buildPaymentLinkEmailRequest(
        recipientEmail: string,
        policyNumber: string,
        paymentURL: string,
        zipcode: string
    ): PaymentLinkEmailRequest {
        const dynamicPaymentURL = this.buildPaymentUrl(paymentURL, policyNumber, zipcode);
        const token = this.tokenService.getToken() || getAuthToken();
        
        return {
            email: recipientEmail,
            policyNumber,
            requestType: 'Payment-Link',
            emailTemplate: 'Payment_Link_External',
            PaymentURL: dynamicPaymentURL,
            token,
            subject: 'Your PSIC requested link for policy payment is ready',
            zipcode
        };
    }

    private isValidEmail(res: EmailVerificationResponse): boolean {
        return this.VALID_STATUSES.includes(res.status) ||
               (res.status === 'unknown' && res.sub_status === 'greylisted') ||
               (res.status === 'do_not_mail' && ['role_based', 'role_based_catch_all'].includes(res.sub_status || ''));
    }

    private getErrorMessage(res: EmailVerificationResponse): string {
        const messages: Record<string, string> = {
            invalid: 'This email address is invalid. Please check and try again.',
            disposable: 'Disposable email addresses are not allowed.',
            toxic: 'This email address cannot be used.',
            do_not_mail: 'This email address cannot be used for communication.',
            spamtrap: 'This email address is not valid for communication.'
        };
        return messages[res.sub_status || ''] || messages[res.status] || 'Unable to verify this email address. Please use a different one.';
    }

    private buildEmailRequest(
        requestType: string,
        subject: string,
        documents: Array<{ attachmentDocId?: string; fileName: string }>,
        recipientEmail: string
    ): EmailDocumentRequest {
        // Use token from TokenService (handles both CSR and TP)
        const token = this.tokenService.getToken() || getAuthToken();
        
        return {
            requestType,
            subject,
            esignFlag: false,
            program: 'HO3',
            queryParam: documents.map(doc => ({ attachmentDocId: doc.attachmentDocId || doc.fileName })),
            email: recipientEmail,
            token
        };
    }

    private buildPaymentUrl(baseUrl: string, policyNumber: string, zipcode: string): string {
        return `${baseUrl}?Category=PolicyNumber&search=${policyNumber}&Zipcode=${zipcode}`;
    }

    


}
